<template>
  <header
    id="header-nav"
    ref="header"
    class="no-cursor header-nav white"
    :class="`${navOpen ? 'nav-open' : ''} ${
      device === 'tablet' || device === 'mobile' ? 'mobile' : ''
    } ${sticky ? 'sticky-header' : ''} `">
    <nav ref="navWrapper">
      <div class="branding">
        <div
          id="header-logos"
          class="logo-container">
          <a
            href="/"
            @click="close(false)">
            <img
              v-if="site.site_logo?.url"
              :src="site.site_logo?.url || ''"
              alt="site-logo"
              class="branding-green" />
            <img
              v-if="site.site_logo_white?.url"
              :src="site.site_logo_white?.url || ''"
              alt="site-logo"
              class="branding-white" />
          </a>
        </div>
        <!-- Label -->
        <div class="header-label-container">
          <div
            id="header-label-container"
            ref="labelContainer"
            class="header-label">
            <Label
              id="page-label"
              text-id="page-label-text"
              :title="router.currentRoute.value.meta.title"
              color="white" />
          </div>

          <!-- Hamburguer -->
          <div
            ref="circleNav"
            class="mobile-nav"
            :class="{ 'footer-green': isFooterGreen }"
            @click="toggle">
            <div
              ref="menuAnimation"
              class="menu-animation" />
          </div>
          <!-- end of Hamburguer -->
        </div>
        <!-- end of Label -->
      </div>
      <div class="nav-container-wrapper">
        <!-- Navigation Logo -->
        <div class="navigation-logo">
          <div
            ref="navigationLogo"
            class="navigation-logo-container">
            <img
              class="w-[191px] mx-auto"
              src="@/assets/img/green_logomark.svg"
              alt="Capstan Logo" />
          </div>
        </div>
        <!-- end of Navigation Logo -->

        <div
          class="navs-container"
          data-lenis-prevent>
          <div class="blue-background"></div>

          <ul
            ref="nav"
            class="nav-container">
            <template v-if="introShown">
              <transition-group
                v-for="(item, index) in menu.menuItems.nodes"
                :key="item.id"
                tag="li"
                appear
                class="nav-container-items"
                name="fade"
                @click="item.cssClasses[0] === '' ? close(false) : null">
                <NavMenuItem
                  :key="item.id"
                  :data-ind="index"
                  :item="item"
                  :mobile="device === 'mobile' ? 'mobile-menu-show' : ''"
                  @click="toggle" />
              </transition-group>
            </template>
          </ul>

          <div class="address-container">
            <div
              class="address"
              v-html="site.address"></div>
            <div class="social">
              <a
                target="_blank"
                :href="site.linkedin"
                title="Linkedin"
                >Linkedin</a
              >
            </div>
          </div>
        </div>
      </div>
    </nav>
  </header>
</template>

<script setup>
// import { DotLottieVue } from '@lottiefiles/dotlottie-vue'
import navAnimation from '@/assets/lottie/menu.json?url'
import NavMenuItem from './NavMenuItem.vue'
import Label from '@/components/utility/Label.vue'
import { ref, watch, onMounted } from 'vue'
import { useStore } from '@/stores/main'
import useUtils from '@/composables/useUtils.js'
import useIntroShown from '@/composables/useIntroShown.js'
import useSmoothScroll from '@/composables/useSmoothScroll.js'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import useShowMenu from '@/composables/useShowMenu.js'
import lottie from 'lottie-web'
import { useRouter } from 'vue-router'
gsap.registerPlugin(ScrollTrigger)

const { menuShown } = useShowMenu()
const store = useStore()
const menu = ref(store.menu({ name: 'main-menu' }))
const site = ref(store.acfOptions())
const header = ref()
const sticky = ref()
const navOpen = ref()
const circleNav = ref()
const labelContainer = ref()
const transitioning = ref(false)
const scrollPosition = ref(0)
const { device } = useUtils()
const { introShown } = useIntroShown()
const { smoothScroll } = useSmoothScroll()
const router = useRouter()
const isFooterGreen = ref(false)
let observer = null

const menuAnimation = ref()
const menuToggle = ref()

const initMenuAnimation = () => {
  const animation = lottie.loadAnimation({
    container: menuAnimation.value,
    renderer: 'svg',
    autoplay: false,
    loop: false,
    path: navAnimation,
  })

  menuToggle.value = animation
}

const initFooterObserver = () => {
  const footer = document.getElementById('site-footer')

  if (footer) {
    if (observer) {
      observer.disconnect()
    }

    observer = new MutationObserver(() => {
      if (footer.classList.contains('green')) {
        isFooterGreen.value = true
        observer.disconnect()
      } else {
        isFooterGreen.value = false
      }
    })

    observer.observe(footer, { attributes: true, attributeFilter: ['class'] })
  }
}

watch(
  () => [menuShown.value],
  ([newVal]) => {
    if (newVal) {
      gsap.fromTo(
        circleNav.value,
        {
          y: -100,
        },
        {
          y: 0,
          duration: 1.8,
          ease: 'power4.inOut',
        }
      )
    }
  }
)

watch(scrollPosition, (newVal) => {
  scrollPosition.value = newVal
  if (newVal > 20) {
    sticky.value = true
  } else {
    sticky.value = false
  }
})

onMounted(() => {
  smoothScroll.on('scroll', (e) => {
    handleScroll(e)
  })

  initFooterObserver()

  initMenuAnimation()
})

const handleScroll = (e) => {
  scrollPosition.value = e.animatedScroll // Adjust the threshold as needed
}

const toggle = () => {
  navOpen.value = !navOpen.value
}

const close = (data) => {
  if (data !== undefined) navOpen.value = data
  else navOpen.value = !navOpen.value
}

// Timeline
const timeline = () => {
  const timeline = gsap.timeline({
    paused: true,
    onComplete: () => {
      transitioning.value = false
    },
  })

  timeline.fromTo(
    '.nav-container-wrapper',
    { top: '100vh', opacity: 0 },
    { top: '0', opacity: 1, duration: 0.5, delay: 0, ease: 'power1.inOut' }
  )
  timeline.fromTo(
    '.navigation-logo-container',
    { opacity: 0 },
    { opacity: 1, duration: 0.5, delay: 0.1, ease: 'power1.inOut' }
  )
  timeline.fromTo(
    '.blue-background',
    { scale: 0 },
    { scale: 1, duration: 0.5, delay: 0, ease: 'power1.inOut' },
    '-=0.6'
  )
  timeline.fromTo(
    '.item-border',
    { width: '0%' },
    { width: '100%', duration: 0.2, delay: 0, ease: 'power1.inOut' },
    '-=0.2'
  )
  timeline.fromTo(
    '.menu-items',
    { opacity: 0 },
    {
      duration: 0.5,
      opacity: 1,
      delay: 0,
    },
    '<'
  )
  timeline.fromTo(
    '.menu-items a',
    { opacity: 0, y: '100%' },
    {
      y: '0%',
      duration: 0.05,
      opacity: 1,
      stagger: 0.05,
      delay: 0,
    },
    '<'
  )
  timeline.fromTo(
    '.address-container',
    { opacity: 0, y: '100%' },
    {
      y: '0%',
      duration: 0.5,
      opacity: 1,
    },
    '<'
  )

  return timeline
}
// End of Timeline

// Reverse timeline
const reverseTimeline = () => {
  const timeline = gsap.timeline({
    paused: true,
    onComplete: () => {
      setTimeout(() => {
        transitioning.value = false
      }, 1000)
    },
  })
  timeline.fromTo(
    '.address-container',
    { opacity: 1, y: '0%' },
    {
      y: '100%',
      duration: 0.25,
      opacity: 0,
    }
  )
  timeline.fromTo(
    '.menu-items a',
    { opacity: 1, y: '0%' },
    {
      y: '350%',
      duration: 0.1,
      opacity: 0,
      stagger: 0.08,
    },
    0
  )
  timeline.fromTo(
    '.menu-items',
    { opacity: 1 },
    {
      duration: 0.3,
      opacity: 0,
    },
    0
  )
  timeline.fromTo(
    '.item-border',
    { width: '100%' },
    { width: '0%', duration: 0.5, ease: 'power1.inOut' },
    0
  )
  timeline.fromTo(
    '.blue-background',
    { scale: 1 },
    { scale: 0, duration: 0.5, ease: 'power1.inOut' },
    0.3
  )
  timeline.fromTo(
    '.navigation-logo-container',
    { opacity: 1 },
    { opacity: 0, duration: 0.5, ease: 'power1.inOut' },
    0.3
  )
  timeline.fromTo(
    '.nav-container-wrapper',
    { top: '0' },
    {
      top: '100vh',
      opacity: 0,
      duration: 0.15,
      ease: 'power1.inOut',
    },
    0.3
  )

  return timeline
}
// End of Reverse timeline

watch(
  () => [navOpen.value],
  ([newVal]) => {
    transitioning.value = true
    if (newVal === true) {
      timeline().play(0)
      smoothScroll.stop()
      menuToggle.value.playSegments([0, 57], true)
    } else if (newVal === false) {
      smoothScroll.start()
      reverseTimeline().play(0)
      menuToggle.value.playSegments([57, 0], true)
    }
    initFooterObserver()
  }
)
</script>

<style lang="scss">
#header-nav {
  @apply fixed left-0 right-0 top-0 pointer-events-none;

  #header-logos {
    @apply opacity-100 pointer-events-auto transition-opacity;
  }

  nav {
    @apply relative;
  }
  .nav-container-wrapper {
    @apply pointer-events-none absolute left-0 top-[100vh] h-screen opacity-0 w-screen origin-top bg-white transition-all delay-0 duration-500 ease-in-out
    z-2 py-1 lg:flex lg:flex-row;

    .navigation-logo {
      @apply p-4 hidden lg:flex lg:w-[40%] lg:justify-center lg:items-center;
    }

    .navs-container {
      @apply w-full relative h-full flex overflow-scroll flex-col justify-start gap-[86px] pt-36 pb-16 lg:gap-[100px] lg:w-[60%] px-4 lg:px-20 xl:px-[15%];

      .blue-background {
        @apply fixed right-sm left-sm top-sm bottom-sm pointer-events-none lg:right-1 lg:left-[40%] lg:bottom-1 lg:top-1 rounded-2xl bg-capstan-sky origin-bottom-right;
      }

      .nav-container {
        @apply mx-auto flex h-auto flex-col justify-start items-start;

        .nav-container-items {
          @apply bg-transparent opacity-100;
        }
      }

      .address-container {
        @apply flex flex-col px-sm lg:flex-row opacity-0 z-2 relative lg:gap-[100px];

        .address {
          * {
            @apply body-copy-2 text-capstan-green;
          }
        }

        .social {
          @apply flex flex-col;

          a {
            @apply body-copy-2 text-capstan-green m-0;
          }
        }
      }
    }
  }
}

#header-nav.sticky-header {
  // @apply backdrop-blur;
  #header-logos {
    @apply opacity-0 pointer-events-none;
  }
}

#header-nav.nav-open {
  @apply z-30 max-h-[100vh] lg:max-h-[130px];

  .header-label-container {
    .mobile-nav {
      &:before {
        @apply bg-capstan-green;
      }

      .line-one {
        @apply -translate-y-9;
      }
      .line-three {
        @apply translate-y-9;
      }
      .line-two {
        @apply w-full;

        &:before {
          @apply bg-white;
        }
      }
    }
  }

  .nav-container-wrapper {
    @apply pointer-events-auto top-0 opacity-100;

    .navs-container {
      .nav-container {
        .nav-container-items {
          .menu-items {
            a {
              &:before {
                @apply opacity-100;
              }
            }
          }
        }
      }
    }
  }

  .menu-animation {
    svg {
      path {
        @apply stroke-white stroke-1;
      }
    }
  }
}

.header-nav {
  @apply top-0 z-10 mb-0 mt-0 flex w-screen items-stretch justify-center bg-transparent lg:overflow-visible max-w-full;

  nav {
    @apply flex flex-col w-screen max-w-full items-stretch pt-0 transition-all duration-300 ease-in-out lg:flex-row lg:items-center lg:justify-between lg:px-0 lg:py-0;

    .nav-container {
      @apply my-0 flex flex-col items-center justify-start px-sm lg:flex-row lg:justify-end lg:px-0;
      li {
        @apply w-full border-capstan-green text-center;

        &:first-child {
          @apply border-t-0;
        }
      }
    }
  }
  .branding {
    @apply flex w-full items-center justify-between pt-3 lg:pt-4 px-5 text-center shadow-none md:text-left lg:w-[242px] lg:flex-[1_0_auto];

    .logo-container {
      @apply flex h-full transition-transform items-center z-1 lg:inline-block xl:max-h-full;

      a {
        @apply relative;

        img {
          @apply h-full max-h-[60px] w-[170px] object-cover object-center;

          &.branding-white {
            @apply hidden;
          }
          &.branding-green {
            @apply flex;
          }
        }

        .header-logo-svg {
          path {
            @apply fill-white;
          }
        }
      }

      &.white-logo-selected {
        a {
          img {
            &.branding-white {
              @apply flex;
            }

            &.branding-green {
              @apply hidden;
            }
          }
        }
      }
    }

    .header-label-container {
      @apply flex flex-row z-3 gap-3 items-center;

      .header-label {
        @apply hidden lg:block;
      }

      .mobile-nav {
        @apply cursor-pointer pointer-events-auto relative gap-2 w-[62px] h-[62px] lg:w-[72px] lg:h-[72px] float-right flex flex-col justify-center rounded-full overflow-hidden items-center;

        &:before {
          @apply content-[''] -z-1 rounded-full absolute h-full w-full bg-white left-0 top-0 transition-all duration-300 origin-center;
        }

        .line {
          @apply transition-all duration-[0.5s] ease-in-out;

          &:before {
            @apply block h-[2px] w-[26px] lg:w-[30px] bg-capstan-green content-[''];
          }

          &.line-one {
            &:before {
              @apply w-[19px] lg:w-[22px];
            }
          }

          &.line-two {
            &:before {
              // @apply transition-colors duration-150 delay-200;
            }
          }

          &.line-three {
            &:before {
              @apply w-[12px] lg:w-[14px];
            }
          }
        }

        &:hover {
          &:before {
            @apply bg-capstan-green scale-[85%];
          }

          .line {
            &:before {
              @apply bg-capstan-warm;
            }
          }
        }

        &.footer-green {
          &:hover {
            &:before {
              @apply bg-capstan-dark;
            }
          }
        }

        .menu-animation {
          svg {
            path {
              @apply stroke-capstan-green;
            }
          }
        }

        &:hover {
          .menu-animation {
            svg {
              path {
                @apply stroke-white;
              }
            }
          }
        }
      }
    }
  }

  .nav-container {
    @apply m-0 flex h-full w-full items-start justify-start text-left transition duration-500 ease-in-out;

    .nav-container-items {
      @apply flex w-full items-center text-center relative;

      .item-border {
        @apply absolute bottom-0 right-0 left-0 h-[1px] bg-capstan-purple;
      }

      div.menu-items {
        @apply header-4 p-0 leading-none text-capstan-green flex h-full items-start justify-start m-0 text-left lg:h-auto relative lg:header-2 lg:m-0;

        a {
          @apply leading-loose;
        }
      }
    }
  }
}
</style>
